import React, { FC, useContext, useEffect, useState } from 'react';
import styles from './TransactionDetails.module.scss';
import { useStore, useUpdateEffect } from '../../../../../common';
import { AuthContext } from '../../../../../logic';
import { Button, CloseDark, Modal, Pagination, Table } from 'esa-ui-library';
import { PropertyType } from 'esa-ui-library/lib/types';
import { getRows } from '../../../PlayerTransactions/RowProvider';
import { isEmpty } from 'lodash';
import { RoundHistory } from '../../../PlayerTransactions/RoundHistory/RoundHistory';
import { filterTransactionFieldsByEnv } from './TransactionDetails.helper';
import { getSessionLogs, pingSession, closeSession, handleTransaction } from '../../../../../api';

interface TransactionDetailsProps {
	session: PropertyType;
	onClose: () => void;
}

const TransactionDetails: FC<TransactionDetailsProps> = ({ session, onClose }): JSX.Element => {
	const [isLoading, setIsLoading] = useState(true);
	const [message, setMessage] = useState<PropertyType>({ msg: null, title: null });
	const [state, dispatch] = useStore(false);
	const { transactions, columnsTransactions, transactionsRows, pagesTotalTransactions, currentPageTransactions } =
		state;
	const { user, token } = useContext(AuthContext);
	const { session_id, game_id, game_name, player_id, game_provider_name } = session;
	const filteredSessionFields = filterTransactionFieldsByEnv(columnsTransactions);
	const [selected, setSelected] = useState<PropertyType>({
		transaction: null,
		action: '',
		logs: null,
		logs_sid: null,
		context_len: 20000,
	});
	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

	const onAction = async (action: string, transaction: PropertyType) => {
		let r: any;
		switch (action) {
			case 'round_history':
				setSelected({
					transaction,
					action,
					logs: null,
					context_len: selected.context_len,
					logs_sid: selected.logs_sid,
				});
				break;
			case 'fail':
			case 'succeed':
				r = await handleTransaction(token, transaction, action);
				setMessage({ msg: 'Code: ' + r?.data?.resultCode + ', message: ' + r?.data?.return, title: 'Info' });
				break;
			default:
				break;
		}
	};

	const onSessionAction = async (action: string): Promise<void> => {
		let r: any;
		switch (action) {
			case 'get_logs':
				if (selected.logs_sid === session_id) {
					selected.context_len *= 2;
				} else {
					selected.context_len = 20000;
					selected.logs_sid = session_id;
				}
				r = await getSessionLogs({ token, session_id, context_len: selected.context_len });
				if (r?.data?.resultCode === 0) {
					setSelected({
						transaction: null,
						action: '',
						logs: r.data?.rawBody,
						context_len: selected.context_len,
						logs_sid: selected.logs_sid,
					});
				} else {
					setMessage({ msg: 'Problem retrieving session logs. Please try again.', title: 'Error' });
				}
				//dispatch('SESSION_LOGS_GET', { token, session_id });
				break;
			case 'ping_session':
			case 'ding_session':
				r = await pingSession({ token, session_id }, action === 'ping_session');
				setMessage({ msg: 'Code: ' + r?.data?.resultCode + ', message: ' + r?.data?.return, title: 'Info' });
				break;
			case 'close_session':
				r = await closeSession({ token, session_id });
				setMessage({ msg: 'Code: ' + r?.data?.resultCode + ', message: ' + r?.data?.return, title: 'Info' });
				break;
			default:
				break;
		}
	};

	const handleCloseModal = () => {
		setSelected({
			transaction: null,
			action: '',
			logs: null,
			context_len: selected.context_len,
			logs_sid: selected.logs_sid,
		});
	};

	const handleCloseErrorModal = () => {
		setMessage({ msg: null, title: null });
	};

	const handleShowCloseConfirmation = () => {
		setShowCloseConfirmation(true);
	};

	const handleConfirmCloseSession = async () => {
		setShowCloseConfirmation(false);
		await onSessionAction('close_session');
	};

	const handleCancelCloseSession = () => {
		setShowCloseConfirmation(false);
	};

	useEffect(() => {
		dispatch('TRANSACTIONS_INIT', { token, page: 1, sessionID: session_id });
	}, []);

	useEffect(() => {
		dispatch('UPDATE_TRANSACTIONS_ROWS', getRows(transactions, filteredSessionFields, user as PropertyType, onAction));
	}, [transactions]);

	useUpdateEffect(() => {
		setIsLoading(false);
	}, [transactions]);

	const onPageChange = (page: number | string) => {
		dispatch('TRANSACTIONS_INIT', { token, page, sessionID: session_id });
	};

	return (
		<section className={styles.DetailsWrapper}>
			<section className={styles.DetailsContent} data-testid='transaction-details'>
				<div className={styles.Header}>
					<div data-testid='transaction-details-header'>
						<span>{`Session ID: ${session_id} | `}</span>
						<span>{`Player ID: ${player_id} | `}</span>
						<span>{`Game: ${game_name} (${game_provider_name}) | `}</span>
						<span>{`Game ID: ${game_id}`}</span>
						{user?.permissions?.canHandleSessions && (
							<span>
								<span style={{ marginLeft: '10px' }}>
									<Button dataId='get_logs' small primary onClick={() => onSessionAction('get_logs')}>
										Get Logs
									</Button>
								</span>
								<span style={{ marginLeft: '10px' }}>
									<Button dataId='ping_session' small primary onClick={() => onSessionAction('ping_session')}>
										Ping Session
									</Button>
								</span>
								<span style={{ marginLeft: '10px' }}>
									<Button dataId='ding_session' small primary onClick={() => onSessionAction('ding_session')}>
										DING Session
									</Button>
								</span>
								<span style={{ marginLeft: '10px' }}>
									<Button dataId='close_session' small primary onClick={handleShowCloseConfirmation}>
										Close Session
									</Button>
								</span>
							</span>
						)}
					</div>
					<CloseDark size={24} className={styles.Close} onClick={onClose} />
				</div>
				<section className={styles.TableWrapper}>
					<Table
						titles={filteredSessionFields}
						data={transactionsRows}
						className={styles.TransactionTable}
						rowClassName={styles.TransactionRow}
						skeletonRows={3}
						isLoading={isLoading}
					/>
					{!!transactions?.length && (
						<Pagination
							total={Math.ceil(pagesTotalTransactions / 10)}
							current={currentPageTransactions}
							onChange={onPageChange}
						/>
					)}
				</section>
			</section>
			{!isEmpty(selected?.transaction) && (
				<Modal
					dataId='round-history-modal'
					primary
					confirm
					title='Round History'
					submitLabel='Got it'
					onCancel={handleCloseModal}
					onSubmit={handleCloseModal}
				>
					<RoundHistory transaction={selected?.transaction} />
				</Modal>
			)}
			{!isEmpty(selected?.logs) && (
				<Modal
					dataId='session-logs-modal'
					primary
					confirm
					title='Session Logs'
					submitLabel='Done'
					onCancel={handleCloseModal}
					onSubmit={handleCloseModal}
				>
					<pre
						style={{
							whiteSpace: 'pre-wrap',
							wordWrap: 'break-word',
							maxHeight: '500px',
							overflow: 'auto',
						}}
					>
						{selected?.logs}
					</pre>
				</Modal>
			)}
			{!isEmpty(message?.msg) && (
				<Modal
					dataId='error-modal'
					primary
					confirm
					title={message?.title}
					submitLabel='OK'
					onCancel={handleCloseErrorModal}
					onSubmit={handleCloseErrorModal}
				>
					<p>{message?.msg}</p>
				</Modal>
			)}
			{showCloseConfirmation && (
				<Modal
					dataId='close-session-confirmation-modal'
					primary
					title='Close Session'
					confirm
					submitLabel='Yes, Close Session'
					onCancel={handleCancelCloseSession}
					onSubmit={handleConfirmCloseSession}
				>
					<p>Are you sure you want to close this session? This action cannot be undone.</p>
				</Modal>
			)}
		</section>
	);
};

export { TransactionDetails };
